// ==========================================================
// Mock Data Service — Multi-Species
// ==========================================================

import type {
  SensorReading, DeviceStatus, DailySummary, AlertEvent,
  DayHourlyDetail, OutdoorWeather, AmbientReading,
  MasterMix, ClimateInsight, DeviceEvent, DailyClimatePoint,
} from '../types';

// ── Today's readings ──

export function getTodayReadings(): SensorReading[] {
  return [
    { timestamp: '06:00', temperature: 17.8, humidity: 91, co2: 580 },
    { timestamp: '07:00', temperature: 17.5, humidity: 92, co2: 610 },
    { timestamp: '08:00', temperature: 18.0, humidity: 89, co2: 650 },
    { timestamp: '09:00', temperature: 18.3, humidity: 87, co2: 720 },
    { timestamp: '10:00', temperature: 18.8, humidity: 86, co2: 790 },
    { timestamp: '11:00', temperature: 19.2, humidity: 85, co2: 860 },
    { timestamp: '12:00', temperature: 19.5, humidity: 88, co2: 940 },
    { timestamp: '13:00', temperature: 19.8, humidity: 90, co2: 1050 },
    { timestamp: '14:00', temperature: 19.1, humidity: 91, co2: 820 },
    { timestamp: '15:00', temperature: 18.6, humidity: 92, co2: 680 },
    { timestamp: '16:00', temperature: 18.2, humidity: 93, co2: 620 },
    { timestamp: '17:00', temperature: 17.9, humidity: 94, co2: 590 },
    { timestamp: '18:00', temperature: 17.6, humidity: 93, co2: 570 },
    { timestamp: '19:00', temperature: 17.4, humidity: 92, co2: 555 },
  ];
}

export function getCurrentReading(): SensorReading {
  const readings = getTodayReadings();
  return readings[readings.length - 1];
}

// ── Devices ──

export function getDeviceStatuses(): DeviceStatus[] {
  return [
    { id: 'inkbird-itc308', name: 'Inkbird ITC-308 WiFi', type: 'heater', source: 'inkbird_itc308', isOnline: true, isActive: false, lastToggled: '2026-04-12T14:30:00', automationEnabled: true },
    { id: 'inkbird-ihc200', name: 'Inkbird IHC-200 WiFi', type: 'humidifier', source: 'inkbird_ihc200', isOnline: true, isActive: true, lastToggled: '2026-04-12T18:45:00', automationEnabled: true },
    { id: 'tuya-fae-fan', name: 'Taze Hava Fanı (FAE)', type: 'fan_fae', source: 'tuya_plug', isOnline: true, isActive: false, lastToggled: '2026-04-12T14:02:00', powerDraw: 12, automationEnabled: true },
    { id: 'tuya-circ-fan', name: 'Sirkülasyon Fanı', type: 'fan_circulation', source: 'tuya_plug', isOnline: true, isActive: true, lastToggled: '2026-04-12T06:00:00', powerDraw: 8, automationEnabled: true },
    { id: 'tuya-humidifier', name: 'Ultrasonik Nemlendirici', type: 'humidifier', source: 'tuya_plug', isOnline: true, isActive: true, lastToggled: '2026-04-12T17:20:00', powerDraw: 25, automationEnabled: true },
    { id: 'tuya-grow-light', name: 'LED Grow Işık (6500K)', type: 'light', source: 'tuya_plug', isOnline: true, isActive: true, lastToggled: '2026-04-12T06:00:00', powerDraw: 8, automationEnabled: true },
  ];
}

// ── Daily summaries ──

export function getDailySummaries(): DailySummary[] {
  return [
    { date: '2026-04-06', dayNumber: 1, avgTemp: 18.1, minTemp: 16.8, maxTemp: 19.5, avgHumidity: 89, minHumidity: 84, maxHumidity: 94, maxCo2: 780, avgCo2: 620, healthScore: 94, alerts: 0, faeActivations: 3, activeBagCount: 3 },
    { date: '2026-04-07', dayNumber: 2, avgTemp: 18.4, minTemp: 17.2, maxTemp: 20.1, avgHumidity: 87, minHumidity: 82, maxHumidity: 93, maxCo2: 920, avgCo2: 690, healthScore: 88, alerts: 1, faeActivations: 5, activeBagCount: 3 },
    { date: '2026-04-08', dayNumber: 3, avgTemp: 17.9, minTemp: 16.5, maxTemp: 19.2, avgHumidity: 91, minHumidity: 87, maxHumidity: 95, maxCo2: 710, avgCo2: 580, healthScore: 96, alerts: 0, faeActivations: 2, activeBagCount: 4 },
    { date: '2026-04-09', dayNumber: 4, avgTemp: 18.6, minTemp: 17.0, maxTemp: 20.8, avgHumidity: 86, minHumidity: 80, maxHumidity: 92, maxCo2: 1080, avgCo2: 740, healthScore: 72, alerts: 3, faeActivations: 7, activeBagCount: 5 },
    { date: '2026-04-10', dayNumber: 5, avgTemp: 18.0, minTemp: 16.9, maxTemp: 19.4, avgHumidity: 90, minHumidity: 86, maxHumidity: 94, maxCo2: 850, avgCo2: 640, healthScore: 91, alerts: 0, faeActivations: 4, activeBagCount: 5 },
    { date: '2026-04-11', dayNumber: 6, avgTemp: 17.7, minTemp: 16.2, maxTemp: 18.9, avgHumidity: 92, minHumidity: 88, maxHumidity: 95, maxCo2: 690, avgCo2: 560, healthScore: 97, alerts: 0, faeActivations: 2, activeBagCount: 5 },
    { date: '2026-04-12', dayNumber: 7, avgTemp: 18.3, minTemp: 17.4, maxTemp: 19.8, avgHumidity: 91, minHumidity: 85, maxHumidity: 94, maxCo2: 1050, avgCo2: 700, healthScore: 82, alerts: 2, faeActivations: 6, activeBagCount: 5 },
  ];
}

// ── Device Events (mock history) ──

export function getDeviceEvents(): DeviceEvent[] {
  return [
    { id: 'de1', deviceId: 'inkbird-ihc200', deviceName: 'Inkbird IHC-200 WiFi', deviceType: 'humidifier',  reason: 'Nem %84 — alt sınır (85%) aşıldı',           startedAt: '2026-04-13T06:12:00', endedAt: '2026-04-13T06:39:00', durationMinutes: 27 },
    { id: 'de2', deviceId: 'tuya-fae-fan',   deviceName: 'Taze Hava Fanı (FAE)', deviceType: 'fan_fae',    reason: 'CO₂ 920 ppm — üst sınır (1000 ppm) yaklaştı', startedAt: '2026-04-13T08:55:00', endedAt: '2026-04-13T09:10:00', durationMinutes: 15 },
    { id: 'de3', deviceId: 'inkbird-ihc200', deviceName: 'Inkbird IHC-200 WiFi', deviceType: 'humidifier',  reason: 'Nem %83 — alt sınır (85%) aşıldı',           startedAt: '2026-04-13T09:44:00', endedAt: '2026-04-13T10:18:00', durationMinutes: 34 },
    { id: 'de4', deviceId: 'tuya-fae-fan',   deviceName: 'Taze Hava Fanı (FAE)', deviceType: 'fan_fae',    reason: 'CO₂ 1050 ppm — kritik eşik (1000 ppm) aşıldı', startedAt: '2026-04-13T13:00:00', endedAt: '2026-04-13T13:22:00', durationMinutes: 22 },
    { id: 'de5', deviceId: 'inkbird-itc308', deviceName: 'Inkbird ITC-308 WiFi', deviceType: 'heater',      reason: 'Sıcaklık 14.8°C — alt sınır (15°C) altına düştü', startedAt: '2026-04-13T03:30:00', endedAt: '2026-04-13T04:05:00', durationMinutes: 35 },
    { id: 'de6', deviceId: 'tuya-humidifier', deviceName: 'Ultrasonik Nemlendirici', deviceType: 'humidifier', reason: 'Nem %82 — alt sınır (85%) aşıldı',          startedAt: '2026-04-13T15:20:00', endedAt: '2026-04-13T15:58:00', durationMinutes: 38 },
    { id: 'de7', deviceId: 'tuya-fae-fan',   deviceName: 'Taze Hava Fanı (FAE)', deviceType: 'fan_fae',    reason: 'Manuel — kullanıcı tarafından açıldı',          startedAt: '2026-04-13T17:30:00', endedAt: undefined, durationMinutes: undefined },
  ];
}

// ── Alerts ──

export function getRecentAlerts(): AlertEvent[] {
  return [
    { id: 'a1', timestamp: '2026-04-12T13:02:00', severity: 'critical', metric: 'co2', message: 'CO₂ seviyesi 1050 ppm — Kritik eşik aşıldı! FAE fanı otomatik devreye girdi.', value: 1050, threshold: 1000, resolved: true },
    { id: 'a2', timestamp: '2026-04-12T11:15:00', severity: 'warning', metric: 'humidity', message: 'Nem %85 sınırına düştü. Nemlendirici çıkış gücü artırıldı.', value: 85, threshold: 85, resolved: true },
    { id: 'a3', timestamp: '2026-04-09T16:40:00', severity: 'critical', metric: 'co2', message: 'CO₂ 1080 ppm — Mercanlaşma riski! Acil havalandırma başlatıldı.', value: 1080, threshold: 1000, resolved: true },
  ];
}

// ── Day hourly detail ──

function generateDayHours(date: string, dayNumber: number, baseTemp: number, baseHum: number, baseCo2: number, spikeHour?: number, spikeCo2?: number): DayHourlyDetail {
  const readings: SensorReading[] = [];
  const alerts: AlertEvent[] = [];
  for (let h = 0; h < 24; h++) {
    const hour = h.toString().padStart(2, '0') + ':00';
    const tempOffset = Math.sin((h - 6) * Math.PI / 12) * 1.5;
    const humOffset = -Math.sin((h - 6) * Math.PI / 12) * 4;
    let co2 = Math.round(baseCo2 + Math.sin((h - 4) * Math.PI / 10) * 150);
    if (spikeHour !== undefined && spikeCo2 !== undefined && h === spikeHour) co2 = spikeCo2;
    if (spikeHour !== undefined && h === spikeHour + 1) co2 = Math.round(baseCo2 + 80);
    const temp = Math.round((baseTemp + tempOffset) * 10) / 10;
    const hum = Math.round(Math.min(98, Math.max(75, baseHum + humOffset)));
    co2 = Math.max(380, co2);
    readings.push({ timestamp: hour, temperature: temp, humidity: hum, co2 });
    if (co2 >= 1000) alerts.push({ id: `${date}-co2-${h}`, timestamp: `${date}T${hour}`, severity: 'critical', metric: 'co2', message: `CO₂ ${co2} ppm — Kritik eşik aşıldı!`, value: co2, threshold: 1000, resolved: true });
    if (hum < 85 && hum >= 80) alerts.push({ id: `${date}-hum-${h}`, timestamp: `${date}T${hour}`, severity: 'warning', metric: 'humidity', message: `Nem %${hum} — Alt sınıra yaklaştı.`, value: hum, threshold: 85, resolved: true });
  }
  return { date, dayNumber, readings, alerts };
}

export function getDayHourlyDetail(date: string, dayNumber = 1): DayHourlyDetail | null {
  const dayMap: Record<string, () => DayHourlyDetail> = {
    '2026-04-06': () => generateDayHours('2026-04-06', 1, 18.1, 89, 600),
    '2026-04-07': () => generateDayHours('2026-04-07', 2, 18.4, 87, 660, 15, 920),
    '2026-04-08': () => generateDayHours('2026-04-08', 3, 17.9, 91, 560),
    '2026-04-09': () => generateDayHours('2026-04-09', 4, 18.6, 86, 700, 16, 1080),
    '2026-04-10': () => generateDayHours('2026-04-10', 5, 18.0, 90, 620),
    '2026-04-11': () => generateDayHours('2026-04-11', 6, 17.7, 92, 540),
    '2026-04-12': () => generateDayHours('2026-04-12', 7, 18.3, 91, 660, 13, 1050),
  };
  if (dayMap[date]) return dayMap[date]();
  // Synthetic fallback for any date — seed from date string for stable values
  const seed = date.split('-').reduce((acc, s) => acc + parseInt(s, 10), 0);
  const baseTemp = 17 + (seed % 6);
  const baseHum  = 82 + (seed % 10);
  const baseCo2  = 550 + (seed % 200);
  const hasCo2Spike = seed % 5 === 0;
  return generateDayHours(date, dayNumber, baseTemp, baseHum, baseCo2, hasCo2Spike ? 13 : undefined, hasCo2Spike ? 1050 : undefined);
}

// ── Weather ──

export function getOutdoorWeather(): OutdoorWeather {
  return { city: 'İstanbul', temperature: 14, humidity: 68, condition: 'Parçalı Bulutlu' };
}

// ── Ambient room reading (the room where the cabinet is) ──

export function getAmbientReading(): AmbientReading {
  return { temperature: 21.8, humidity: 54, co2: 420, updatedAt: '19:00' };
}

// ── Climate insights ──

export function generateClimateInsights(data: DailyClimatePoint[]): ClimateInsight[] {
  const insights: ClimateInsight[] = [];
  for (let i = 1; i < data.length; i++) {
    const prev = data[i - 1], curr = data[i];
    // Bag count change correlation
    if (curr.activeBagCount && prev.activeBagCount && curr.activeBagCount > prev.activeBagCount) {
      if (curr.avgCo2 > prev.avgCo2 + 30) {
        insights.push({ date: curr.date, type: 'bag_change', severity: 'info', message: `Torba sayısı ${prev.activeBagCount}→${curr.activeBagCount} arttı, CO₂ ortalaması ${prev.avgCo2}→${curr.avgCo2} ppm yükseldi.` });
      }
    }
    // CO2 spike
    if (curr.maxCo2 >= 1000) {
      insights.push({ date: curr.date, type: 'co2_spike', severity: 'warning', message: `Maks CO₂ ${curr.maxCo2} ppm — ${curr.activeBagCount || '?'} aktif torba ile kritik eşik aşıldı. FAE sıklığı artırılmalı.` });
    }
    // Humidity drop
    if (curr.avgHumidity < 85) {
      insights.push({ date: curr.date, type: 'humidity_low', severity: 'warning', message: `Ort. nem %${curr.avgHumidity} — Dış ortam neminin düşük olması nemlendirici kapasitesini zorluyor olabilir.` });
    }
  }
  return insights;
}

// ── Master Mixes ──

const MASTER_MIXES: MasterMix[] = [
  {
    id: 'mix-1', name: 'Standart Meşe (LM)', speciesId: 'lion-mane', totalWeightKg: 5,
    ingredients: [
      { name: 'Meşe Talaşı', percentage: 40, gramsCalculated: 2000 },
      { name: 'Buğday Kepeği', percentage: 20, gramsCalculated: 1000 },
      { name: 'Soya Unu', percentage: 5, gramsCalculated: 250 },
      { name: 'Su', percentage: 35, gramsCalculated: 1750 },
    ],
    notes: 'Standart aslan yelesi substratı. Nem oranı: %60-65.',
  },
  {
    id: 'mix-2', name: 'Kayın Karışımı (SH)', speciesId: 'shiitake', totalWeightKg: 4,
    ingredients: [
      { name: 'Kayın Talaşı', percentage: 45, gramsCalculated: 1800 },
      { name: 'Yulaf Kepeği', percentage: 15, gramsCalculated: 600 },
      { name: 'Soya Unu', percentage: 5, gramsCalculated: 200 },
      { name: 'Su', percentage: 35, gramsCalculated: 1400 },
    ],
    notes: 'Shiitake için optimize edilmiş kayın bazlı formül.',
  },
];

export function getMasterMixes(): MasterMix[] { return MASTER_MIXES; }
